import MDBox from "components/MDBox";
import { Card, Tab, Tabs, Chip, Menu, MenuItem, Button, List, ListItem, ListItemText, Alert, Box, Typography, Collapse, IconButton } from "@mui/material";
import { Error as ErrorIcon, Warning as WarningIcon, ExpandMore, ExpandLess } from "@mui/icons-material";
import { useEffect, useState, useContext } from "react";
import { AuthContext } from "common/AuthProvider";
import { Role } from "common/Constants";
import moment from "moment";
import tripService from "services/tripService";
import { useParams } from "react-router-dom";
import TripDetails from "./TripDetails";
import TripHeaderEdit from "./header/TripHeaderEdit";
import TripHeaderView from "./header/TripHeaderView";
import orgDataService from "services/orgDataService";
import MapEditLayout from "./MapEditLayout";
import React from "react";
import TripDialog from "./TripModal";
import TripViewSkeleton from "./TripDetailsSkeleton";
import { useDispatch, useSelector } from "react-redux";
import { tripData, getTrip } from "store/tripSlice";
import { toast } from "react-toastify";
import CloserLayout from "./closer/CloserLayout";
import { hasResetChip, TripResetChip, useTripResetStatus } from "./closer/tripResetStatus";
import OtpVerification from "./OtpVerification";
import TripSummary from "./TripSummary";
import GpsReconstructLayout from "./GpsReconstructLayout";
import AutoClosePreviewLayout from "./AutoClosePreviewLayout";
import { ACCESS_AUTO_APPROVE_PREVIEW, canSeeAutoApprovePreview } from "trips/permissions";

const TripView = () => {
  const dispatch = useDispatch();
  const params = useParams();
  // GPS Reconstruction is open to everyone who can reach the trip view (the /trips route admits Admin and
  // TripAdmin). Auto-Approve Preview needs Admin or the AccessAutoApprovePreview permission.
  const { isUserInRole, userHasPermission } = useContext(AuthContext);
  const isAdmin = isUserInRole(Role.Admin);
  const [isLoading, setIsLoading] = useState(true);
  const trip = useSelector(tripData);
  // Header chip: last Event Reset (and its auto-approve verdict), or that a bulk reset holds the trip.
  const { status: tripResetStatus } = useTripResetStatus(trip?.id);
  const canOpenResetHistory = isAdmin;
  // The Auto-Approve tab exists to get an unapproved trip approved — hide it once the trip is approved.
  const canSeeAutoApprove = canSeeAutoApprovePreview(
    isAdmin,
    userHasPermission(ACCESS_AUTO_APPROVE_PREVIEW),
    trip?.isApproved === true,
  );
  const [activeTab, setActiveTab] = useState(0);
  // Tab indices are position-based and the Auto-Approve (5) tab is conditional, so a stale activeTab
  // (e.g. sitting on Auto-Approve when the trip becomes approved) can point past the last visible tab.
  // isTabVisible tells us if the current index still exists.
  const isTabVisible = (i: number) => i <= 4 || (i === 5 && canSeeAutoApprove);
  const [visitedTabs, setVisitedTabs] = useState(new Set([0])); // Track which tabs have been visited
  const [isHeaderEdit, setIsHeaderEdit] = useState(false);
  const [pointsOfService, setPointsOfService] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [reason, setReason] = useState(undefined);
  const [dialogTitle, setDialogTitle] = useState(undefined);
  const [dialogSubTitle, setDialogSubTitle] = useState(undefined);
  const [dialogReasons, setDialogReasons] = useState([]);
  const [cancelReasons, setCancelReasons] = useState([]);
  const [declineReasons, setDeclineReasons] = useState([]);
  const [validationErrors, setValidationErrors] = useState([]);
  const [validationWarnings, setValidationWarnings] = useState([]);
  const [showValidationErrors, setShowValidationErrors] = useState(true);
  const [expandedValidationErrors, setExpandedValidationErrors] = useState(true);

  useEffect(() => {
    orgDataService.getTripCancelReasons().then((reasons) => setCancelReasons(reasons));
    orgDataService.getTripDeclineReasons().then((reasons) => setDeclineReasons(reasons));
  }, []);

  useEffect(() => {
    if (!params.tripNumber) return;
    setIsLoading(true);
    dispatch(getTrip(params.tripNumber));
  }, [params.tripNumber]);

  useEffect(() => {
    if (!trip) return;

    if(pointsOfService?.length === 0){
      loadPointsOfService(trip.clientId, trip.hub?.id).finally(() => setIsLoading(false));
    } else{
      setIsLoading(false);
    }
  }, [trip]);

  // When the open tab stops existing — e.g. a trip becomes approved (incl. right after approving it on the
  // Auto-Approve tab) and that tab hides itself — fall back to the Trip tab so the user sees the refreshed
  // details instead of a blank panel.
  useEffect(() => {
    if (!isTabVisible(activeTab)) setActiveTab(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, canSeeAutoApprove]);

  const loadPointsOfService = async (clientId, hubId) => {
    var results = await orgDataService.getPointOfService(clientId, hubId)
     setPointsOfService(results);
  };
  const handleChange = (event: React.SyntheticEvent | null, newValue: number) => {
    setActiveTab(newValue);
    setVisitedTabs(prev => new Set([...prev, newValue]));
  };

  // After an Auto-Approve apply commits, the trip's events + approval changed. The header/summary re-read
  // from the getTrip the Auto-Approve panel dispatches, but the other tabs (Map/Closer/OTP/GPS) stay mounted
  // via visitedTabs and would keep showing pre-apply data until a full page reload. Drop every tab except the
  // current one so each re-fetches fresh the next time it's opened.
  const handleAutoCloseApplied = () => setVisitedTabs(new Set([activeTab]));

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleMenuSelect = (e) => {
    setAnchorEl(null);
    switch (e.currentTarget.innerText) {
      case "Edit Header":
        setIsHeaderEdit(!isHeaderEdit);
        break;
      case "Accept":
        setIsLoading(true);
        tripService.accept(trip.id).then(() => dispatch(getTrip(params.tripNumber)));
        break;
      case "Cancel":
        setDialogTitle("Cancel Trip");
        setDialogSubTitle("Select a reason the trip was canceled.");
        setDialogReasons(cancelReasons);
        setOpenDialog(true);
        break;
      case "Decline":
        setDialogTitle("Decline Trip");
        setDialogSubTitle("Select a reason the trip was declined.");
        setDialogReasons(declineReasons);
        setOpenDialog(true);
        break;
      case "Clone":
        setIsLoading(true);
        tripService.clone(trip.id).then(() => dispatch(getTrip(params.tripNumber)));
        break;
      case "Split":
        setIsLoading(true);
        tripService.split(trip.id).then(() => dispatch(getTrip(params.tripNumber)));
        break;
      case "Validate":
        setIsLoading(true);
        tripService.validate(trip.id).then((e) => {
          const allItems = e.errors || [];
          const errors = allItems.filter((item) => item.errorType !== "Warning");
          const warnings = allItems.filter((item) => item.errorType === "Warning");
          setValidationErrors(errors);
          setValidationWarnings(warnings);
          setShowValidationErrors(true);
          setExpandedValidationErrors(true);
          if (errors.length === 0 && warnings.length === 0) {
            toast.success("Trip validation passed successfully.");
          } else if (errors.length === 0 && warnings.length > 0) {
            toast.success("Trip validation passed.");
            toast.warning(`${warnings.length} warning${warnings.length === 1 ? '' : 's'} found.`);
          } else {
            toast.warning(`Trip validation found ${errors.length} error${errors.length === 1 ? '' : 's'}.`);
          }
        }).catch((error) => {
          toast.error("Trip validation failed.");
        }).finally(() => setIsLoading(false));
        break;
    }
  };
  const onAcceptDialog = (reason) => {
    setIsLoading(true);
    if (dialogTitle == "Decline Trip") {
      tripService.decline(trip.id, reason).then(() => dispatch(getTrip(params.tripNumber)));
    } else if (dialogTitle === "Cancel Trip") {
      tripService.cancel(trip.id, reason).then(() => dispatch(getTrip(params.tripNumber)));
    }
  };
  return (
    <>
      <Card>
        {(trip?.workflow?.tags || hasResetChip(tripResetStatus)) && (
          //const uniqueTags = _.uniqBy(props.trip.workflow.tags || [], (x) => x.description);
          <MDBox sx={{ display: "flex", flexDirection: "row-reverse" }}>
            <TripResetChip
              status={tripResetStatus}
              canOpenHistory={canOpenResetHistory}
              sx={{ width: { xs: "auto", sm: "15%" }, m: 1 }}
            />
            {trip?.workflow?.tags?.map((t) => {
              return (
                <Chip
                  key={t.description}
                  label={t.description}
                  color={t.isError ? "error" : undefined}
                  sx={{ width: { xs: "auto", sm: "15%" }, m: 1 }}
                />
              );
            })}
          </MDBox>
        )}
        {validationErrors.length > 0 && showValidationErrors && (
          <MDBox sx={{ m: 2 }}>
            <Alert 
              severity="error" 
              icon={<ErrorIcon />}
              onClose={() => setShowValidationErrors(false)}
              sx={{ 
                mb: 2,
                '& .MuiAlert-message': {
                  width: '100%'
                }
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="h6" component="div" sx={{ fontWeight: 'bold' }}>
                  Validation Errors ({validationErrors.length})
                </Typography>
                <IconButton 
                  size="small" 
                  onClick={() => setExpandedValidationErrors(!expandedValidationErrors)}
                  sx={{ ml: 1 }}
                >
                  {expandedValidationErrors ? <ExpandLess /> : <ExpandMore />}
                </IconButton>
              </Box>
              <Collapse in={expandedValidationErrors}>
                <Box component="ul" sx={{ 
                  margin: 0, 
                  paddingLeft: 2,
                  '& li': {
                    marginBottom: 0.5,
                    fontSize: '0.875rem',
                    lineHeight: 1.4
                  }
                }}>
                  {validationErrors.map((error, index) => (
                    <li key={index}>{error.message}</li>
                  ))}
                </Box>
              </Collapse>
            </Alert>
          </MDBox>
        )}
        {validationWarnings.length > 0 && showValidationErrors && (
          <MDBox sx={{ m: 2 }}>
            <Alert
              severity="warning"
              icon={<WarningIcon />}
              onClose={() => setShowValidationErrors(false)}
              sx={{
                mb: 2,
                '& .MuiAlert-message': {
                  width: '100%'
                }
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="h6" component="div" sx={{ fontWeight: 'bold' }}>
                  Validation Warnings ({validationWarnings.length})
                </Typography>
              </Box>
              <Box component="ul" sx={{
                margin: 0,
                paddingLeft: 2,
                '& li': {
                  marginBottom: 0.5,
                  fontSize: '0.875rem',
                  lineHeight: 1.4
                }
              }}>
                {validationWarnings.map((warning, index) => (
                  <li key={index}>{warning.message}</li>
                ))}
              </Box>
            </Alert>
          </MDBox>
        )}
        <TripSummary />
        <Tabs
          value={isTabVisible(activeTab) ? activeTab : 0}
          onChange={handleChange}
          aria-label="basic tabs"
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
        >
          <Tab
            className={activeTab == 0 ? "tab tab-active" : "tab"}
            label="Trip"
            id="trip-tab"
            aria-controls="trip-tab-panel"
          />
          <Tab
            className={activeTab == 1 ? "tab tab-active" : "tab"}
            label="Map"
            id="map-tab"
            aria-controls="map-tab-panel"
          />
         <Tab
            className={activeTab == 2 ? "tab tab-active" : "tab"}
            label="Closer"
            id="closer-tab"
            aria-controls="closer-tab-panel"
          />
         <Tab
            className={activeTab == 3 ? "tab tab-active" : "tab"}
            label="OTP Verification"
            id="otp-verification-tab"
            aria-controls="otp-verification-tab-panel"
          />
         <Tab
            className={activeTab == 4 ? "tab tab-active" : "tab"}
            label="GPS Reconstruction"
            id="gps-reconstruction-tab"
            aria-controls="gps-reconstruction-tab-panel"
          />
         {canSeeAutoApprove && (
         <Tab
            className={activeTab == 5 ? "tab tab-active" : "tab"}
            label="Auto-Approve Preview"
            id="autoclose-preview-tab"
            aria-controls="autoclose-preview-tab-panel"
          />
         )}
        </Tabs>
        <div id="driver-tab-panel" hidden={activeTab !== 0}>
          <MDBox px={2}>
            {isLoading ? (
              <TripViewSkeleton />
            ) : (
              <>
                {isHeaderEdit && (
                  <TripHeaderEdit
                    setIsHeaderEdit={setIsHeaderEdit}
                    pointsOfService={pointsOfService}
                    loadPointsOfService={loadPointsOfService}
                  />
                )}
                {!isHeaderEdit && (
                  <>
                    <MDBox display="flex" justifyContent="flex-end" flexWrap="wrap" gap={1} mb={0}>
                      {trip?.clientName?.toUpperCase() === "BNSF-RIDE" && (
                        <Button
                          variant="contained"
                          onClick={() => {
                            window.open(
                              `/reports/popup/trip-details/${trip?.id ?? ""}`,
                              "_blank",
                              "width=1536,height=720"
                            );
                          }}
                          sx={{
                            mr: 2,
                            backgroundColor: '#000',
                            color: '#fff',
                            '&:hover': {
                              backgroundColor: '#333',
                            }
                          }}
                        >
                          BNSF Trip Details
                        </Button>
                      )}
                      <Button
                        id="basic-button"
                        aria-controls={open ? "basic-menu" : undefined}
                        aria-haspopup="true"
                        aria-expanded={open ? "true" : undefined}
                        onClick={handleClick}
                      >
                        Actions
                      </Button>
                      <Menu
                        id="basic-menu"
                        anchorEl={anchorEl}
                        open={open}
                        onClose={handleMenuSelect}
                        MenuListProps={{
                          "aria-labelledby": "basic-button",
                        }}
                      >
                        <MenuItem onClick={handleMenuSelect}>Edit Header</MenuItem>
                        <MenuItem onClick={handleMenuSelect}>Accept</MenuItem>
                        <MenuItem onClick={handleMenuSelect}>Cancel</MenuItem>
                        <MenuItem onClick={handleMenuSelect}>Decline</MenuItem>
                        <MenuItem onClick={handleMenuSelect}>Clone</MenuItem>
                        <MenuItem disabled={trip?.relatedTrips?.length > 0} onClick={handleMenuSelect}>
                          Split
                        </MenuItem>
                        <MenuItem onClick={handleMenuSelect}>Validate</MenuItem>
                      </Menu>
                    </MDBox>
                    <TripHeaderView trip={trip} />
                  </>
                )}
                <TripDetails />{" "}
              </>
            )}
          </MDBox>
        </div>
        <div id="map-tab-panel" hidden={activeTab !== 1}>
          <MDBox p={1}>
            {visitedTabs.has(1) && (
              <MapEditLayout tripNumber={params.tripNumber} isHidden={activeTab !== 1} />
            )}
          </MDBox>
        </div>
        <div id="closer-tab-panel" hidden={activeTab !== 2}>
          <MDBox p={1}>
            {visitedTabs.has(2) && <CloserLayout />}
          </MDBox>
        </div>
        <div id="otp-verification-tab-panel" hidden={activeTab !== 3}>
          <MDBox p={1}>
            {visitedTabs.has(3) && <OtpVerification />}
          </MDBox>
        </div>
        <div id="gps-reconstruction-tab-panel" hidden={activeTab !== 4}>
          <MDBox p={1}>
            {visitedTabs.has(4) && (
              <GpsReconstructLayout tripNumber={params.tripNumber} isHidden={activeTab !== 4} />
            )}
          </MDBox>
        </div>
        <div id="autoclose-preview-tab-panel" hidden={activeTab !== 5}>
          <MDBox p={1}>
            {canSeeAutoApprove && visitedTabs.has(5) && (
              <AutoClosePreviewLayout
                tripNumber={params.tripNumber}
                isHidden={activeTab !== 5}
                onOpenCloser={() => handleChange(null, 2)}
                onApplied={handleAutoCloseApplied}
              />
            )}
          </MDBox>
        </div>
      </Card>
      <TripDialog
        title={dialogTitle}
        subTitle={dialogSubTitle}
        value={reason}
        setValue={setReason}
        open={openDialog}
        setOpen={setOpenDialog}
        options={dialogReasons}
        onAccept={onAcceptDialog}
      />
    </>
  );
};
export default TripView;
